import { useEffect, useMemo, useRef } from 'react';

const DEFAULT_TIMEOUT_MINUTES = 15;
const MIN_TIMEOUT_MS = 60_000;

/** Purpose: Resolves inactivity timeout from env with safe fallback. Inputs: none. Output: timeout milliseconds. */
function resolveTimeoutMs() {
  const timeoutMinutes = Number.parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT_MINUTES || '', 10);
  if (Number.isFinite(timeoutMinutes) && timeoutMinutes > 0) {
    return Math.max(timeoutMinutes * 60_000, MIN_TIMEOUT_MS);
  }
  return DEFAULT_TIMEOUT_MINUTES * 60_000;
}

/** Purpose: Logs out active users after inactivity window. Inputs: { enabled, onTimeout }. Output: none. */
export default function useInactivityLogout({ enabled, onTimeout }) {
  const timeoutMs = useMemo(() => resolveTimeoutMs(), []);
  const timerRef = useRef(null);
  const timedOutRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);

  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled) {
      timedOutRef.current = false;
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const resetTimer = () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        if (timedOutRef.current) return;
        timedOutRef.current = true;
        onTimeoutRef.current();
      }, timeoutMs);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      resetTimer();
    };

    const activityEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'focus'];
    for (const eventName of activityEvents) {
      window.addEventListener(eventName, resetTimer, { passive: true });
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    resetTimer();

    return () => {
      for (const eventName of activityEvents) {
        window.removeEventListener(eventName, resetTimer);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      timedOutRef.current = false;
    };
  }, [enabled, timeoutMs]);
}
